import React, { useEffect, useState, useRef } from 'react';
import QRCode from 'qrcode';
import ReactQuill, { Quill as QuillNS } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { certificateTemplatesAPI } from '../../services/api';


// Register font sizes for Quill
const Size = QuillNS.import('formats/size');
Size.whitelist = ['10px', '11px', '12px', '14px', '16px', '18px', '24px', '32px'];
QuillNS.register(Size, true);

// Inject custom CSS for Quill size dropdown and apply pixel sizes to text
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before { content: '10px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="11px"]::before { content: '11px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before { content: '12px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before { content: '14px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before { content: '16px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before { content: '18px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before { content: '24px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before { content: '32px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before { content: '10px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="11px"]::before { content: '11px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: '12px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: '14px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: '16px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: '18px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before { content: '24px'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: '32px'; }

    .ql-size-10px { font-size: 10px !important; }
    .ql-size-11px { font-size: 11px !important; }
    .ql-size-12px { font-size: 12px !important; }
    .ql-size-14px { font-size: 14px !important; }
    .ql-size-16px { font-size: 16px !important; }
    .ql-size-18px { font-size: 18px !important; }
    .ql-size-24px { font-size: 24px !important; }
    .ql-size-32px { font-size: 32px !important; }
  `;
  document.head.appendChild(style);
}

const quillModules = {
  toolbar: [
    [{ 'font': [] }],
    [{ 'size': Size.whitelist }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub' }, { 'script': 'super' }],
    [{ 'header': [1, 2, 3, false] }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    ['link', 'image'],
  ]
};



// Helper to render template string with variables, preserving Quill formatting
function renderTemplateWithQuill(html, data) {
  if (!html) return '';
  // Replace only {{variable}} inside text nodes, not tags
  // Use a DOM parser for safe replacement
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      node.textContent = node.textContent.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
        return data[key] !== undefined ? data[key] : '';
      });
    } else if (node.childNodes) {
      node.childNodes.forEach(walk);
    }
  }
  walk(doc.body);
  return doc.body.innerHTML;
}

// Helper to format date as '16th day of July, 2025'
function formatLongDate(dateObj) {
  if (!dateObj) return '';
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const day = dateObj.getDate();
  const suffix = (d => (d > 3 && d < 21) ? 'th' : ['th', 'st', 'nd', 'rd', 'th'][Math.min(d % 10, 4)])(day);
  return `${day}${suffix} day of ${months[dateObj.getMonth()]}, ${dateObj.getFullYear()}`;
}

// Helper to format birthdate as 'October 16, 1991'
function formatBirthdate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr; // fallback to raw if invalid
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

const CertificatePrintPreviewModal = ({ open, onClose, document }) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorValue, setEditorValue] = useState('');
  const [backgroundImage, setBackgroundImage] = useState(null); // Data URL
  const previewRef = useRef();
  const fileInputRef = useRef();
  // Handle background image upload
  const handleBackgroundImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new window.FileReader();
      reader.onload = (ev) => {
        setBackgroundImage(ev.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Prepare variables for the template
  const pascalCase = (str) =>
    typeof str === 'string' && str.length > 0
      ? str.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      : '';

  const getPreviewData = (doc) => {
    const resident = doc.resident || {};

    // Compose resident name as "Firstname M. Lastname Suffix"
    const middleInitial = resident.middle_name
      ? `${resident.middle_name.charAt(0).toUpperCase()}.`
      : '';
    const suffix = resident.suffix ? ` ${resident.suffix}` : '';
    const residentName =
      resident.first_name && resident.last_name
        ? `${pascalCase(resident.first_name)}${middleInitial ? ' ' + middleInitial : ''} ${pascalCase(resident.last_name)}${suffix}`
        : doc.resident_name || '';
    return {
      resident_name: residentName,
      address: resident.full_address,
      age: resident.age || '',
      civil_status: pascalCase(resident.civil_status || ''),
      gender: pascalCase(resident.gender || ''),
      pronouns: resident.gender === 'male' ? 'He/Him' : resident.gender === 'female' ? 'She/Her' : '',
      birthdate: formatBirthdate(resident.birthdate || ''),
      birthplace: resident.birthplace || '',
      nationality: resident.nationality || '',
      occupation: resident.occupation || '',
      purpose: doc.purpose || '',
      issued_by: 'Barangay Captain',
      date: formatLongDate(doc.issued_at ? new Date(doc.issued_at) : new Date()),
      tracking_number: doc.tracking_number || '',
      fee_amount: doc.fee_amount !== undefined && doc.fee_amount !== null ? `₱ ${parseFloat(doc.fee_amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
      remarks: doc.remarks || '',
      photo_url: resident.photo_url || null,
    };
  };

  useEffect(() => {
    if (!open || !document) return;
    setLoading(true);
    certificateTemplatesAPI.getAll().then(({ data }) => {
      let tpl = data.find(t => t.document_type_id === document.document_type_id);
      if (!tpl && document.type) {
        tpl = data.find(t => t.document_type_key === document.type);
      }
      setTemplate(tpl || null);
      setEditorValue(tpl ? tpl.body : '');
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [open, document]);

  if (!open) return null;


  const previewData = document ? getPreviewData(document) : {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate-in fade-in duration-200 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl relative p-4 sm:p-6 animate-in slide-in-from-bottom duration-300 max-h-[95vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center mb-4 sm:mb-6 flex-shrink-0">
          <h3 className="text-lg sm:text-xl font-medium text-gray-900">Certificate Preview</h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl transition-all duration-300 transform hover:scale-125 hover:rotate-90 active:scale-95"
          >
            ×
          </button>


        </div>
        {/* Background image upload */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <label className="font-sm text-gray-700 whitespace-nowrap">Letterhead</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleBackgroundImageChange}
              className="block rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 file:font-semibold file:cursor-pointer w-full sm:w-auto"
              style={{ maxWidth: '100%' }}
            />
            {backgroundImage && (
              <button
                type="button"
                className="text-xs text-red-500 border border-red-500 px-3 sm:px-4 py-2 sm:py-3 rounded hover:bg-red-50 transition-colors duration-200 whitespace-nowrap"
                onClick={() => {
                  setBackgroundImage(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                Remove
              </button>
            )}
          </div>
          <button
            type="button"
            className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg text-sm sm:text-base font-medium hover:bg-blue-700 transition-all duration-200 shadow w-full sm:w-auto"
            onClick={async () => {
              const printContents = renderTemplateWithQuill(editorValue, previewData);
              const qrValue = previewData.tracking_number || 'Barangay360';
              let qrDataUrl = '';
              try {
                qrDataUrl = await QRCode.toDataURL(qrValue, { width: 100, margin: 0 });
              } catch (err) {
                qrDataUrl = '';
              }
              let printFrame = window.document.getElementById('certificate-print-iframe');
              if (!printFrame) {
                printFrame = window.document.createElement('iframe');
                printFrame.id = 'certificate-print-iframe';
                printFrame.style.position = 'fixed';
                printFrame.style.right = '0';
                printFrame.style.bottom = '0';
                printFrame.style.width = '0';
                printFrame.style.height = '0';
                printFrame.style.border = 'none';
                printFrame.style.visibility = 'hidden';
                window.document.body.appendChild(printFrame);
              }
              const doc = printFrame.contentWindow.document;
              // Custom Quill font size CSS for print
              const quillFontSizes = [10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32];
              const quillFontSizeCSS = quillFontSizes.map(size => `
                  .ql-size-${size}px, .ql-editor .ql-size-${size}px {
                    font-size: ${size}px !important;
                  }
                `).join('');
              doc.open();
              doc.write(`
                  <html>
                    <head>
                      <title>Certificate Print</title>
                      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/quill/dist/quill.snow.css" />
                      <style>
                        @page { size: A4; margin: 0; }
                        html, body {
                          width: 210mm;
                          height: 297mm;
                          margin: 0 !important;
                          padding: 0 !important;
                          background: #fff;
                          color: #222;
                          font-family: Arial, sans-serif;
                          line-height: 1.2;
                          overflow: hidden;
                        }
                        .a4-paper {
                          width: 210mm;
                          height: 297mm;
                          margin: 0 !important;
                          padding: 0 !important;
                          background: #fff;
                          position: relative;
                          min-height: 297mm;
                          overflow: hidden;
                        }
                        .ql-editor {
                          padding: 0 !important;
                          background: transparent;
                        }
                        .qr-box {
                          position: absolute;
                          right: 80px;
                          bottom: 80px;
                          width: 20mm;
                          height: 20mm;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          background: #fff;
                        }
                        .bg-img-print {
                          position: absolute;
                          top: 0;
                          left: 0;
                          width: 210mm;
                          height: 297mm;
                          object-fit: cover;
                          z-index: 0;
                          pointer-events: none;
                          opacity: 1;
                        }
                        ${quillFontSizeCSS}
                      </style>
                    </head>
                    <body>
                      <div class="a4-paper ql-editor" style="position:relative;">
                        ${backgroundImage ? `<img src='${backgroundImage}' class='bg-img-print' alt='Background' id='bg-img-print' />` : ''}
                        ${previewData.photo_url ? `<div style="position:absolute; top:200px; right:30px; width:96px; height:128px; z-index:2; border:1px solid #ccc; overflow:hidden;"><img src='${previewData.photo_url}' style="width:100%; height:100%; object-fit:cover;" onerror="this.parentElement.style.display='none';" /></div>` : ''}
                        <div style="position:relative;z-index:1; padding:1rem; ${previewData.photo_url ? 'padding-top: 10px;' : ''}">
                          ${printContents}
                        </div>
                        <div class="qr-box">
                          ${qrDataUrl ? `<img src='${qrDataUrl}' alt='QR Code' style='width:32mm;height:32mm;' id='qr-img-print' />` : ''}
                        </div>
                      </div>
                      <script>
                        function waitForImagesAndPrint() {
                          var images = Array.from(document.images);
                          if (!images.length) {
                            window.print();
                            setTimeout(function() {
                              if (window.frameElement && window.frameElement.parentNode) {
                                window.frameElement.parentNode.removeChild(window.frameElement);
                              }
                            }, 500);
                            return;
                          }
                          var loaded = 0;
                          var done = false;
                          function check() {
                            loaded++;
                            if (loaded >= images.length && !done) {
                              done = true;
                              setTimeout(function() {
                                window.print();
                                setTimeout(function() {
                                  if (window.frameElement && window.frameElement.parentNode) {
                                    window.frameElement.parentNode.removeChild(window.frameElement);
                                  }
                                }, 500);
                              }, 200);
                            }
                          }
                          images.forEach(function(img) {
                            if (img.complete) {
                              check();
                            } else {
                              img.onload = check;
                              img.onerror = check;
                            }
                          });
                        }
                        window.onload = waitForImagesAndPrint;
                      </script>
                    </body>
                  </html>
                `);
              doc.close();
              printFrame.contentWindow.focus();
              // Do NOT call printFrame.contentWindow.print() here; printing is handled in the iframe only
              // Do NOT remove the iframe here; it is removed from inside the iframe after printing
            }}
          >
            Print Certificate
          </button>
        </div>
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading template...</div>
        ) : template ? (
          <>
            <div className="flex justify-center overflow-hidden flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
              <div
                className="w-full"
                ref={previewRef}
                style={{
                  maxWidth: '210mm',
                  width: '100%',
                  minHeight: '160mm',
                  maxHeight: '100%',
                  background: '#fff',
                  position: 'relative',
                  overflow: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cbd5e1 #f1f5f9'
                }}
              >
                <div className="overflow-x-auto">
                  <ReactQuill
                    theme="snow"
                    value={renderTemplateWithQuill(editorValue, previewData)}
                    onChange={setEditorValue}
                    modules={quillModules}
                    className="bg-white rounded"
                    style={{
                      width: '100%',
                      maxWidth: '100%'
                    }}
                  />
                  <style>{`
                    .ql-container {
                      max-width: 100% !important;
                      overflow-x: auto !important;
                    }
                    .ql-editor {
                      max-width: 100% !important;
                      overflow-x: auto !important;
                      word-wrap: break-word !important;
                      overflow-wrap: break-word !important;
                    }
                    .ql-toolbar {
                      max-width: 100% !important;
                      overflow-x: auto !important;
                    }
                    @media (max-width: 640px) {
                      .ql-toolbar {
                        flex-wrap: wrap !important;
                      }
                      .ql-toolbar .ql-formats {
                        margin-right: 4px !important;
                        margin-bottom: 4px !important;
                      }
                    }
                  `}</style>
                </div>
              </div>
            </div>

          </>
        ) : (
          <div className="text-center py-12 text-gray-400">No certificate template found for this document type.</div>
        )}
      </div>
    </div>
  );
};

export default CertificatePrintPreviewModal;
